/**
 * AI Scribe V2 — docked session window
 *
 * Replaces the V1 centred modal with a fixed, full-bleed overlay that runs the
 * visit live: transcript, extracted clinical events and scribe instructions side
 * by side. It never moves — the only layout change is the minimise toggle.
 *
 * Transport, note generation and note export all reuse the V1 hooks/queries —
 * only the presentation layer is new.
 *
 * @author [Rajkumar Rathod]
 */

import SendOutlinedIcon from "@mui/icons-material/SendOutlined";
import { Box } from "@mui/material";
import { useEffect, useRef, useState } from "react";
import StyledButtonV2 from "@/components/app/StyledButtonV2";
import { DisclosureHost } from "@/hooks/use-disclosure";
import useSelectedPatient from "@/hooks/local-storage/use-selected-patient";
import { useQueryParams } from "@/hooks/use-query-param";
import { ClinicalNotesDTO } from "@/services";
import { VisitNote } from "@/pages/app/provider/patient/patient-list/patient-chart-new/patient-visit-note/encounter/components/ActiveForm";
import { PRIMITIVE, px } from "@/tokens/design-tokens";
import {
  DEFAULT_SCRIBE_NOTE_TYPE,
  SCRIBE_DRAFT_LABELS,
} from "../ai-scriber-constant";
import ScribeSessionBody from "./components/ScribeSessionBody";
import ScribeWindowHeader from "./components/ScribeWindowHeader";
import { SCRIBE_V2_COLORS, SCRIBE_V2_DIMENSIONS } from "./constants";
import { ScribeSessionSnapshot, ScribeTranscriptLine } from "./types";
import { useRestoredSnapshot } from "./use-restored-snapshot";
import { useScribeInstructions } from "./use-scribe-instructions";
import { useScribeNoteActions } from "./use-scribe-note-actions";
import { useScribeSessionPersistence } from "./use-scribe-session-persistence";
import { useScribeSession } from "./useScribeSession";

interface AiScribeV2WindowProps {
  disclosureHost: DisclosureHost;
  /** Name shown on provider bubbles — falls back to "Provider" */
  providerName?: string;
  /**
   * Note template the visit note is currently on. The scribe opens on it so the
   * generated note matches the form the provider is filling, and stays editable
   * from the header picker.
   */
  noteType?: VisitNote;
}

/**
 * The live session itself. Mounted only while the disclosure is open so the
 * audio socket, recorder and timer never run behind a closed window.
 */
interface AiScribeV2SessionProps extends AiScribeV2WindowProps {
  /** Snapshot recovered for this visit, already decrypted (null when none). */
  initialSnapshot: ScribeSessionSnapshot | null;
}

const AiScribeV2Session = ({
  disclosureHost,
  providerName,
  noteType: selectedNoteType,
  initialSnapshot,
}: AiScribeV2SessionProps) => {
  const appointmentId = useQueryParams("appointmentid");
  const [selectedPatient] = useSelectedPatient();

  const [isMinimized, setIsMinimized] = useState(false);
  const [noteType, setNoteType] = useState<VisitNote>(
    (initialSnapshot?.noteType as VisitNote) ??
      selectedNoteType ??
      DEFAULT_SCRIBE_NOTE_TYPE
  );
  const { instructions, setInstructions, instructionText } =
    useScribeInstructions();
  const [draftSegments, setDraftSegments] = useState<ScribeTranscriptLine[]>();
  const [selectedSectionData, setSelectedSectionData] =
    useState<ClinicalNotesDTO | null>(null);

  const patientName = [selectedPatient?.firstName, selectedPatient?.lastName]
    .filter(Boolean)
    .join(" ");

  const {
    status,
    setStatus,
    elapsedSeconds,
    transcript,
    clinicalEvents,
    error,
    wasRestored,
    sessionStartedAt,
    start,
    togglePause,
    stop,
  } = useScribeSession({
    providerName,
    patientName: patientName || undefined,
    patientUuid: selectedPatient?.uuid,
    initialSnapshot,
  });

  useScribeSessionPersistence({
    sessionKey: appointmentId ?? undefined,
    status,
    transcript,
    clinicalEvents,
    elapsedSeconds,
    sessionStartedAt,
    noteType,
  });

  const { handleClose, handleFinish, handleAddToVisit } = useScribeNoteActions({
    transcript,
    noteType,
    patientUuid: selectedPatient?.uuid,
    appointmentId,
    selectedSectionData,
    instructions: instructionText,
    stop,
    setStatus,
    setDraftSegments,
    onCloseWindow: disclosureHost.close,
  });

  /** The window opens straight into a live session — unless one is being recovered. */
  const hasAutoStartedRef = useRef(false);
  useEffect(() => {
    if (hasAutoStartedRef.current) return;
    hasAutoStartedRef.current = true;
    if (wasRestored) return;
    setSelectedSectionData(null);
    start();
  }, [start, wasRestored]);

  /** Surface a denied/unavailable microphone instead of sitting on an empty panel. */
  useEffect(() => {
    if (error) setStatus("ERROR");
  }, [error, setStatus]);

  const isDrafting = status === "FINALIZING" || !!draftSegments;

  const { inset } = SCRIBE_V2_DIMENSIONS;

  /**
   * Positioned against the visit-note panel it is mounted in, never the viewport —
   * the scribe must not cover the patient sidebar or the chart navigation.
   *
   * Expanded: fills the panel, inset from its edges.
   * Minimised: a header-only pill docked to the panel's bottom-right corner.
   * Never draggable — position is entirely derived from `isMinimized`.
   */
  const positionSx = isMinimized
    ? {
        right: px(inset),
        bottom: px(inset),
        width: px(SCRIBE_V2_DIMENSIONS.minimizedWidth),
        maxWidth: `calc(100% - ${px(inset * 2)})`,
        height: SCRIBE_V2_DIMENSIONS.headerHeight,
      }
    : {
        top: px(inset),
        left: px(inset),
        right: px(inset),
        bottom: px(inset),
      };

  return (
    <Box
      sx={{
        position: "absolute",
        ...positionSx,
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        zIndex: SCRIBE_V2_DIMENSIONS.zIndex,
        backgroundColor: SCRIBE_V2_COLORS.windowBg,
        border: `1px solid ${
          status === "LISTENING"
            ? SCRIBE_V2_COLORS.accentLive
            : status === "PAUSED"
              ? SCRIBE_V2_COLORS.accentPaused
              : SCRIBE_V2_COLORS.windowBorderIdle
        }`,
        borderRadius: SCRIBE_V2_DIMENSIONS.radius,
        boxShadow: `0 ${px(PRIMITIVE.shadow.offset[400])} ${px(
          PRIMITIVE.shadow.offset[800]
        )} ${PRIMITIVE.shadow.color.medium}`,
      }}
    >
      <ScribeWindowHeader
        status={status}
        elapsedSeconds={elapsedSeconds}
        noteType={noteType}
        onNoteTypeChange={setNoteType}
        onStop={stop}
        onMinimizeToggle={() => setIsMinimized((previous) => !previous)}
        onClose={handleClose}
        isMinimized={isMinimized}
        isNoteTypeLocked={isDrafting}
        actionSlot={
          isDrafting ? (
            <StyledButtonV2
              variant="primary"
              label={SCRIBE_DRAFT_LABELS.PUSH_TO_EHR}
              sIcon={<SendOutlinedIcon />}
              disabled={Object.keys(selectedSectionData ?? {}).length === 0}
              onClick={handleAddToVisit}
            />
          ) : null
        }
      />

      <ScribeSessionBody
        isMinimized={isMinimized}
        isDrafting={isDrafting}
        noteType={noteType}
        draftSegments={draftSegments}
        onNoteReceived={setSelectedSectionData}
        onRetryNoteGeneration={handleFinish}
        sessionStartedAt={sessionStartedAt}
        transcript={transcript}
        status={status}
        onTogglePause={togglePause}
        clinicalEvents={clinicalEvents}
        instructions={instructions}
        onInstructionsChange={setInstructions}
        onFinish={handleFinish}
      />
    </Box>
  );
};

/** Mount gate — keeps the session (and its websocket) out of the tree until the provider opens the scribe, and holds it while the recovered snapshot decrypts. */
const AiScribeV2Window = (props: AiScribeV2WindowProps) => {
  const appointmentId = useQueryParams("appointmentid");
  const opened = props.disclosureHost.opened;
  const initialSnapshot = useRestoredSnapshot(
    appointmentId ?? undefined,
    opened
  );

  if (!opened || initialSnapshot === undefined) return null;
  return <AiScribeV2Session {...props} initialSnapshot={initialSnapshot} />;
};

export default AiScribeV2Window;
